import { http, HttpResponse, delay } from 'msw';

import { taxesEstimation } from '../data/billingData';
import {
  adhocInvoiceTransaction,
  allowedContractActions,
  estimatedSalesQuote,
  itemizedSalesContract,
  itemizedSalesQuote,
  salesContractsList,
  salesCustomers,
  salesProducts,
  salesQuotes,
} from '../data/internalBillingData';

export const internalBillingHandlers = [
  // quotes
  http.get('*/v3/sales/quotes', async () => {
    await delay();
    return HttpResponse.json({ quotes: salesQuotes }, { status: 200 });
  }),
  http.post('*/v3/sales/quotes', async () => {
    await delay();
    return HttpResponse.json(salesQuotes[0], { status: 201 });
  }),
  http.get('*/v3/sales/quotes/:quoteId', async ({ params }) => {
    const quote = salesQuotes.find(q => q.quote_id === params.quoteId);

    if (!quote) {
      return new HttpResponse(null, { status: 404 });
    }
    await delay();
    return HttpResponse.json(
      { ...itemizedSalesQuote, ...quote },
      { status: 200 }
    );
  }),
  http.patch('*/v3/sales/quotes/:quoteId', async ({ params }) => {
    const quote = salesQuotes.find(q => q.quote_id === params.quoteId);

    if (!quote) {
      return new HttpResponse(null, { status: 404 });
    }
    await delay();
    return HttpResponse.json(quote, { status: 200 });
  }),
  http.post('*/v3/sales/quotes/:quoteId/finalize', async ({ params }) => {
    const quote = salesQuotes.find(q => q.quote_id === params.quoteId);

    if (!quote) {
      return new HttpResponse(null, { status: 404 });
    }
    await delay();
    return HttpResponse.json(quote, { status: 200 });
  }),
  http.post('*/v3/sales/quotes/:quoteId/accept', async ({ params }) => {
    const quote = salesQuotes.find(q => q.quote_id === params.quoteId);

    if (!quote) {
      return new HttpResponse(null, { status: 404 });
    }
    await delay();
    return HttpResponse.json(quote, { status: 200 });
  }),
  http.post('*/v3/sales/quotes/:quoteId/cancel', async ({ params }) => {
    const quote = salesQuotes.find(q => q.quote_id === params.quoteId);

    if (!quote) {
      return new HttpResponse(null, { status: 404 });
    }
    await delay();
    return HttpResponse.json(quote, { status: 200 });
  }),
  http.post('*/v3/sales/quotes/:quoteId/copy', async () => {
    await delay();
    return HttpResponse.json(salesQuotes[0], { status: 201 });
  }),
  http.post('*/v2/sales/quotes/estimate', async () => {
    await delay();
    return HttpResponse.json(estimatedSalesQuote, { status: 200 });
  }),
  http.post('*/v2/sales/quotes/:quoteId/pdf', async () => {
    await delay();
    return new HttpResponse('%PDF-1.4\n%%EOF\n', {
      status: 200,
      headers: { 'Content-Type': 'application/pdf' },
    });
  }),

  // products
  http.get('*/v3/sales/products/currency/:currency', async () => {
    await delay();
    return HttpResponse.json(salesProducts, { status: 200 });
  }),

  // customers (specific routes before `:customerId`)
  http.get('*/v2/sales/customers', async () => {
    await delay();
    return HttpResponse.json({ customers: salesCustomers }, { status: 200 });
  }),
  http.post('*/v2/sales/customers', async () => {
    await delay();
    return HttpResponse.json(salesCustomers[0], { status: 201 });
  }),
  http.get('*/v2/sales/customers/search', async ({ request }) => {
    const searchTerm = (
      new URL(request.url).searchParams.get('q') ?? ''
    ).toLowerCase();
    const customers = salesCustomers.filter(customer =>
      customer.name.toLowerCase().includes(searchTerm)
    );

    await delay();
    return HttpResponse.json({ customers }, { status: 200 });
  }),
  http.get('*/v2/sales/customers/:customerId', async ({ params }) => {
    const customer = salesCustomers.find(
      c => c.customer_id === params.customerId
    );

    if (!customer) {
      return new HttpResponse(null, { status: 404 });
    }
    await delay();
    return HttpResponse.json(customer, { status: 200 });
  }),
  http.patch('*/v2/sales/customers/:customerId', async ({ params }) => {
    const customer = salesCustomers.find(
      c => c.customer_id === params.customerId
    );

    if (!customer) {
      return new HttpResponse(null, { status: 404 });
    }
    await delay();
    return HttpResponse.json(customer, { status: 200 });
  }),
  http.get('*/v3/sales/customers/:customerId/quotes', async ({ params }) => {
    const quotes = salesQuotes.filter(
      q => q.customer.customer_id === params.customerId
    );

    await delay();
    return HttpResponse.json({ quotes }, { status: 200 });
  }),
  http.get('*/v2/sales/customers/:customerId/contracts', async ({ params }) => {
    const contracts = salesContractsList.contracts.filter(
      c => c.customer.customer_id === params.customerId
    );

    await delay();
    return HttpResponse.json({ contracts }, { status: 200 });
  }),

  // contracts
  http.get('*/v2/sales/contracts', async () => {
    await delay();
    return HttpResponse.json(salesContractsList, { status: 200 });
  }),
  http.get('*/v2/sales/contracts/:contractId/allowed_actions', async () => {
    await delay();
    return HttpResponse.json(allowedContractActions, { status: 200 });
  }),
  http.get('*/v2/sales/contracts/:contractId', async ({ params }) => {
    if (params.contractId !== itemizedSalesContract.contract_id) {
      return new HttpResponse(null, { status: 404 });
    }
    await delay();
    return HttpResponse.json(itemizedSalesContract, { status: 200 });
  }),
  ...['amend', 'renew', 'early_renew', 'reinstate'].map(action =>
    http.post(`*/v2/sales/contracts/:contractId/${action}`, async () => {
      await delay();
      return HttpResponse.json(itemizedSalesQuote, { status: 200 });
    })
  ),

  // internal invoices
  http.post('*/v3/organizations/:organizationId/invoices', async () => {
    await delay();
    return HttpResponse.json(adhocInvoiceTransaction, { status: 201 });
  }),
  http.post(
    '*/v2/organizations/:organizationId/billing/admin/transaction/estimate',
    async () => {
      await delay();
      return HttpResponse.json(taxesEstimation, { status: 200 });
    }
  ),
];
